// The sign-in page (D48): email and password, nothing else — there is no registration; the
// owner adds users. The API answers every failure with the same message.
import { type FormEvent, useState } from "react";
import { useLocation } from "react-router";
import { Button, Input, Password } from "rizzui";

import { ApiError } from "../api/queries";
import { browser, nextPath, signIn } from "../auth/api";
import { messages, useT } from "../i18n";
import { LanguageMenu } from "../ui/LanguageMenu";
import { ErrorState } from "../ui/states";
import { SignInLayout } from "./SignInLayout";

function failure(e: unknown): string {
  const t = messages().auth.signIn;
  if (e instanceof ApiError && e.status === 429) return t.tooManyAttempts;
  if (e instanceof ApiError && e.status === 401) return t.wrongCredentials;
  return e instanceof Error ? e.message : String(e);
}

export default function SignInPage() {
  const t = useT().auth.signIn;
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await signIn(email.trim(), password);
      browser.reloadTo(nextPath(location.search));
    } catch (err) {
      setError(failure(err));
      setPassword("");
      setBusy(false);
    }
  }

  return (
    <SignInLayout actions={<LanguageMenu />}>
      <h1 className="text-[28px] tracking-tight lg:text-[32px]">{t.title}</h1>
      <p className="mb-0 mt-2 text-[14px] text-gray-500">
        {t.lead(<span className="font-mono text-[13px] text-gray-700">{window.location.hostname}</span>)}
      </p>

      <form className="mt-8 space-y-5" aria-label={t.title} onSubmit={(e) => void onSubmit(e)}>
        <Input
          type="email"
          size="lg"
          label={t.email}
          placeholder={t.emailPlaceholder}
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
          labelClassName="font-medium"
          inputClassName="text-sm"
        />
        <Password
          size="lg"
          label={t.password}
          placeholder={t.passwordPlaceholder}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          labelClassName="font-medium"
          inputClassName="text-sm"
        />
        {error ? <ErrorState title={t.failed} message={error} /> : null}
        <Button type="submit" size="lg" className="w-full" isLoading={busy}>
          {t.submit}
        </Button>
      </form>

      <div className="mt-8 rounded-xl border border-muted bg-gray-50 p-4 text-[13px] leading-relaxed text-gray-600 dark:bg-gray-100/50">
        <span className="font-semibold text-gray-900">{t.noAccountTitle}</span>
        {t.noAccount(<span className="font-medium text-gray-800">{t.usersPage}</span>)}
      </div>
    </SignInLayout>
  );
}
